import { datadogRum } from '@datadog/browser-rum';
import type { Entry } from 'contentful';
import { AGENT_ID_COOKIE_NAME, makeMiscInfoJsonString, LeadSource } from 'services/analytics/analytics';
import { updateAnalyticsId } from 'services/analytics/heap';
import { readCookie } from 'utils/Cookies';
import { getField, getSys } from 'utils/EntryUtil';
import { Environment } from 'utils/Environment';
import type { FormValues } from './ContactFormTypes';
import { Reseller } from 'api/contentful/contentTypes.cloverdotcom';
import { CookieConfig } from 'services/analytics/CookieConfig';
import { generateEmail, generateLead } from 'services/shop/checkout';

export enum FormEndpointType {
  LEADS = 'Leads',
  CONNECT_TO_SALES = 'Connect_To_Sales',
  PURCHASE_CLOVER_ESSENTIALS = 'Purchase_Clover_Essentials',
  CLOVER_NO_HARDWARE = 'Clover_No_Hardware',
  CLOVER_NO_HARDWARE_PAYMENTS = 'Clover_No_Hardware_Payments',
  CLOVER_HARDWARE = 'Clover_Hardware',
  CLOVER_CAMPAIGN = 'Clover_Marketing_Campaign',
}

export const acquiringFormNameMap = {
  [FormEndpointType.LEADS]: 'Get Started',
  [FormEndpointType.CONNECT_TO_SALES]: 'Demo Request',
  [FormEndpointType.PURCHASE_CLOVER_ESSENTIALS]: 'Clover Essentials',
  [FormEndpointType.CLOVER_NO_HARDWARE]: 'Clover No Hardware',
  [FormEndpointType.CLOVER_NO_HARDWARE_PAYMENTS]: 'Clover No Hardware',
  [FormEndpointType.CLOVER_HARDWARE]: 'Clover Hardware',
  [FormEndpointType.CLOVER_CAMPAIGN]: 'Clover Marketing Campaign',
};

// Reseller Contentful Entry IDs
export const DEV_CLOVER_DIRECT_RESELLER_ENTRY_ID = 'vZlXL53vZEMn89hFBUr1d';
export const PROD_CLOVER_DIRECT_RESELLER_ENTRY_ID = '5aXiIQqQ3uX8z49bkD59VN';

// Reseller Clover IDs
export const DEV_CLOVER_DIRECT_RESELLER_ID = '47HMNNZ9BPF8E';
export const PROD_CLOVER_DIRECT_RESELLER_ID = 'BVG1JKB6RM0DM';

// Order Template UUIDs
const NO_HARDWARE_ESSENTIALS_ORDER_TEMPLATE_CODE = 'NO_HARDWARE_ESSENTIALS';
const NO_HARDWARE_PAYMENTS_ORDER_TEMPLATE_CODE = 'NO_HARDWARE_PAYMENTS';

export const getResellerUuid = (contentfulReseller: Entry<Reseller>) => {
  const resellerId = getField(contentfulReseller, 'resellerId');
  if (resellerId) return resellerId;

  return Environment.current.isCloverProdEnvironment
    ? PROD_CLOVER_DIRECT_RESELLER_ID
    : DEV_CLOVER_DIRECT_RESELLER_ID;
};

export const getResellerEntryId = (contentfulReseller: Entry<Reseller>) => {
  const resellerEntryId = getSys(contentfulReseller, 'id');
  if (resellerEntryId) return resellerEntryId;

  return Environment.current.isCloverProdEnvironment
    ? PROD_CLOVER_DIRECT_RESELLER_ENTRY_ID
    : DEV_CLOVER_DIRECT_RESELLER_ENTRY_ID;
};

async function getEmailAndUpdateAnalyticsId(values: FormValues) {
  const email = values.emailAddress && values.emailAddress.trim();
  await updateAnalyticsId(email);
  return email;
}

async function submitToEndpointAndSendAnalyticsEvents(
  apiRequestPromise: any,
  onSuccess: () => void,
  onError: () => void,
  onLeadError: () => void,
  formName: string,
  email?: string,
  eventTypes = ['form_submit'],
) {
  let responseStatus: number;
  const successfulApiResponse: any = await apiRequestPromise().catch((error: any) => {
    responseStatus = error.response && error.response.status;
    datadogRum.addError(`Unable to submit Lead Gen Forms - ${JSON.stringify(error?.response?.data)}`);
    if (error?.response?.data?.message.includes('Cannot modify immutable lead with UUID:')) {
      onLeadError();
    }
    onError();
  });

  if (successfulApiResponse) {
    eventTypes.forEach(eventType => {
      sendGTMSubmissionEvent(formName, eventType, email);
    });
    responseStatus = successfulApiResponse.status;
    onSuccess();
  }

  eventTypes.forEach(eventType => {
    sendTealiumSubmissionEvent(formName, eventType, responseStatus, email);
  });
}

export const connectToSalesEndpoint = (
  onSuccess: () => void,
  onError: () => void,
  onLeadError: () => void,
  contentfulReseller: Entry<Reseller>,
  cookieConfig: CookieConfig[],
) => {
  return createGrowthLeadWithOrderRequest(
    onSuccess,
    onError,
    onLeadError,
    FormEndpointType.CONNECT_TO_SALES,
    contentfulReseller,
    cookieConfig,
  );
};

export const cloverMarketingCampaignEndpoint = (
  onSuccess: () => void,
  onError: () => void,
  onLeadError: () => void,
  contentfulReseller: Entry<Reseller>,
  cookieConfig: CookieConfig[],
) => {
  return createGrowthLeadWithOrderRequest(
    onSuccess,
    onError,
    onLeadError,
    FormEndpointType.CLOVER_CAMPAIGN,
    contentfulReseller,
    cookieConfig,
  );
};

export const cloverNoHardwareRequestWithEssentials = (
  onSuccess: () => void,
  onError: () => void,
  onLeadError: () => void,
  contentfulReseller: Entry<Reseller>,
  cookieConfig: CookieConfig[],
) => {
  return createGrowthLeadWithOrderRequest(
    onSuccess,
    onError,
    onLeadError,
    FormEndpointType.CLOVER_NO_HARDWARE,
    contentfulReseller,
    cookieConfig,
    NO_HARDWARE_ESSENTIALS_ORDER_TEMPLATE_CODE,
  );
};

export const cloverNoHardwareRequestWithPayments = (
  onSuccess: () => void,
  onError: () => void,
  onLeadError: () => void,
  contentfulReseller: Entry<Reseller>,
  cookieConfig: CookieConfig[],
) => {
  return createGrowthLeadWithOrderRequest(
    onSuccess,
    onError,
    onLeadError,
    FormEndpointType.CLOVER_NO_HARDWARE_PAYMENTS,
    contentfulReseller,
    cookieConfig,
    NO_HARDWARE_PAYMENTS_ORDER_TEMPLATE_CODE,
  );
};

/**
 * Submits a Growth API lead.
 * @param onSuccess A success function to call if the request succeeds
 * @param onError An error function to call if the request fails
 * @param formEndpointType The Form Endpoint Type to use
 * @param contentfulReseller The Contentful Reseller entry
 * @param cookieConfig The Contentful cookieConfig
 * @param orderTemplateCode An optional order template code to submit the form with
 *
 */
const createGrowthLeadWithOrderRequest = (
  onSuccess: () => void,
  onError: () => void,
  onLeadError: () => void,
  formEndpointType: FormEndpointType,
  contentfulReseller: Entry<Reseller>,
  cookieConfig: CookieConfig[],
  orderTemplateCode?: string,
) => {
  return async (values: FormValues, actions: any) => {
    const email = await getEmailAndUpdateAnalyticsId(values);
    const acquiringForm = acquiringFormNameMap[formEndpointType];

    const marketingInfo = JSON.parse(
      makeMiscInfoJsonString(cookieConfig, {
        industry: values.businessType,
        yearsInBusiness: values.yearsInBusiness,
        zipCode: values.zipCode,
        acquiring_form: acquiringForm,
        leadSource: LeadSource.WEB_FORM,
        bestCallTime: values.whenToCall,
      }),
    );

    const leadPayload = {
      email,
      firstName: values.firstName,
      lastName: values.lastName,
      businessName: values.businessName,
      phoneNumber: values.phoneNumber?.replace(/\D/g, ''),
      marketingInfo,
      chainAgentId: readCookie(AGENT_ID_COOKIE_NAME),
      shippingAddress: { zipCode: values.zipCode },
      resellerUuid: getResellerUuid(contentfulReseller),
      annualProcessingVolume: values.annualProcessingVolume,
      activityType: 'formSubmit_c',
      orderTemplateCode,
    };

    const apiRequestPromise = async () => {
      const createdLead = await generateLead(leadPayload);
      return formEndpointType === FormEndpointType.CLOVER_NO_HARDWARE ||
        formEndpointType === FormEndpointType.CLOVER_NO_HARDWARE_PAYMENTS
        ? generateEmail(createdLead.data.uuid)
        : createdLead;
    };

    await submitToEndpointAndSendAnalyticsEvents(
      apiRequestPromise,
      onSuccess,
      onError,
      onLeadError,
      acquiringForm,
      email,
      ['form_submit', 'status'],
    );

    actions.setSubmitting(false);
  };
};

export const FormEndpointMapper = {
  [FormEndpointType.CONNECT_TO_SALES]: connectToSalesEndpoint,
  [FormEndpointType.PURCHASE_CLOVER_ESSENTIALS]: cloverNoHardwareRequestWithEssentials,
  [FormEndpointType.CLOVER_NO_HARDWARE]: cloverNoHardwareRequestWithEssentials,
  [FormEndpointType.CLOVER_NO_HARDWARE_PAYMENTS]: cloverNoHardwareRequestWithPayments,
  [FormEndpointType.CLOVER_CAMPAIGN]: cloverMarketingCampaignEndpoint,
};

export const ApiClientMapper = {
  [FormEndpointType.CONNECT_TO_SALES]: Environment.current.baseApiClient,
  [FormEndpointType.PURCHASE_CLOVER_ESSENTIALS]: Environment.current.cosApiClient,
  [FormEndpointType.CLOVER_NO_HARDWARE]: Environment.current.baseApiClient,
  [FormEndpointType.CLOVER_CAMPAIGN]: Environment.current.baseApiClient,
  [FormEndpointType.CLOVER_NO_HARDWARE_PAYMENTS]: Environment.current.baseApiClient,
};

export function sendGTMSubmissionEvent(formType: string, eventType: string, email?: string) {
  const emailDomain = email && email.split('@')[1];
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventType,
    name: formType,
    heap_id: readCookie('clAnalyticsId'),
    domain: emailDomain,
  });
}

export function sendTealiumSubmissionEvent(
  formType: string,
  eventType: string,
  statusCode: number,
  email?: string,
) {
  const emailDomain = email && email.split('@')[1];
  if (!utag?.link) return;
  utag.link({
    event: eventType,
    name: formType,
    clAnalyticsId: readCookie('clAnalyticsId'),
    submitter_email_domain: emailDomain,
    response_status_code: statusCode,
  });
}
